/**
 * Component: Browse widget surface
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R13
 *
 * Embeddable guideline-browser surface. Wraps the BROWSE-V1
 * endpoints:
 *
 *   - ``GET /api/v1/browse/guidelines`` lists available guidelines.
 *   - ``GET /api/v1/browse/guidelines/{id}`` returns the full
 *     guideline detail for the selected item.
 *
 * AB#4402: layout brought to FRONTEND-WEB-V1 Browse-page parity.
 * Two single-pane views (not the old always-on two-pane):
 *
 *   - **list view** — a "Browse Guidelines" heading + full-width
 *     tiles, one guideline per row (published / updated dates);
 *   - **detail view** — replaces the list when a guideline is
 *     opened; a "← Back to guidelines" link, the guideline title
 *     (large) + dates, a small "Contents (N)" control at title
 *     level that opens the table of contents as a pop-over drawer
 *     (BrowseToc, browse-content.tsx), then the R05 body sections,
 *     R07 recommendation cards, and R08 references list.
 *
 * AB#4647: a "back to top" affordance on the detail view. Once
 * the user has scrolled past the guideline header it returns
 * the page to the title in one click. Visible only when the
 * header is out of view (IntersectionObserver on the header),
 * so it does not clutter the initial above-the-fold layout.
 *
 * Deep-link: when mounted with ``initialGuidelineId`` (the
 * cross-module BrowseLink swapping the host surface to Browse in
 * place — AB#4396/4400) the surface opens straight to that
 * guideline's detail; ``initialSectionId`` then scrolls to the
 * cited section (``#cnt_<id>``) once its DOM has rendered.
 *
 * Risk Links: R7, R11
 * FMEA Links: FM7, FM11
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { WidgetApiClient } from '../api/client';
import { WidgetError } from '../api/errors';
import { trackWidgetUsage } from '../core/usage-tracking';
import {
  BrowseToc,
  ReferencesList,
  SectionContent,
  extractRecommendationColourOption,
  extractReferenceContent,
  extractSectionContent,
} from './browse-content';
import {
  SURFACE_WRAPPER_STYLE,
  SurfaceErrorBanner,
  explainWidgetError,
} from './shared';

/**
 * BROWSE-V1 list item. Field names mirror the Pydantic
 * ``BrowseGuidelineListItem`` camelCase aliases so the widget
 * consumes the JSON response without re-shaping.
 */
interface BrowseGuidelineListItem {
  guidelineId: number;
  guidelineName: string;
  publishDate: string;
  updateDate: string;
  externalGuidelineURL?: string | null;
}

/**
 * BROWSE-V1 detail shape. ``fullContent`` is an opaque upstream
 * payload; the viewer renders the documented metadata fields and
 * leaves the blob untouched for future renderers.
 */
interface BrowseGuidelineDetail {
  guidelineId: number;
  guidelineName: string;
  shortTitle: string;
  publishDate: string;
  updateDate: string;
  externalGuidelineURL?: string | null;
  fullContent: Record<string, unknown>;
}

export interface BrowseSurfaceProps {
  client: WidgetApiClient;
  baseUrl: string;
  token: string;
  onError?: (error: Error) => void;
  /**
   * Deep-link target. When set, the surface opens this guideline's
   * detail on mount (instead of the list) — used when the widget's
   * cross-module BrowseLink swaps the host surface to Browse in
   * place (AB#4396 / AB#4400 in-place navigation).
   */
  initialGuidelineId?: number;
  /**
   * When set alongside ``initialGuidelineId``, the surface scrolls
   * to that section once its DOM has rendered. The section anchor
   * is ``#cnt_<sectionId>`` (see browse-content.tsx).
   */
  initialSectionId?: number;
}

/**
 * AB#4647: walk up from ``el`` to the nearest ancestor that is
 * actually scrollable on the Y axis. Returns ``null`` when no
 * such ancestor exists (i.e. the document scrolls). The widget
 * may be embedded either where the document scrolls (test-site,
 * portal page) or inside a host element with its own
 * ``overflow-y: auto`` chrome; resolving the scroller this way
 * lets a single animation target the right container instead of
 * ``scrollIntoView`` animating every scrollable ancestor at
 * once.
 */
function findScrollContainer(
  el: Element | null,
): HTMLElement | null {
  if (!el) return null;
  if (typeof window === 'undefined') return null;
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * AB#4647: deterministic smooth scroll to the top of the
 * resolved scroll container. The native
 * ``scrollTo({behavior:'smooth'})`` path is opportunistic — it
 * can bail mid-animation when the browser's smooth-scroll
 * heuristics interpret an unrelated scroll event (a wheel tick,
 * a touch-pad inertia residual, a focus-driven container scroll
 * elsewhere on the page) as an "interruption" and snap the
 * animation to a stop partway. Drive the scroll explicitly with
 * ``requestAnimationFrame`` instead: fixed 500 ms duration, an
 * ease-out cubic, and a guaranteed final ``scrollTop = 0`` write
 * regardless of distance from the top.
 */
function smoothScrollToTop(scroller: HTMLElement | Window): void {
  const isWindow = scroller === window;
  const startY = isWindow
    ? window.scrollY
    : (scroller as HTMLElement).scrollTop;
  if (startY <= 0) return;

  const duration = 500;
  const startTime =
    typeof performance !== 'undefined' ? performance.now() : Date.now();
  const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

  function step(now: number): void {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    const y = Math.round(startY * (1 - easeOutCubic(t)));
    if (isWindow) {
      window.scrollTo(0, y);
    } else {
      (scroller as HTMLElement).scrollTop = y;
    }
    if (t < 1) {
      requestAnimationFrame(step);
    }
  }
  requestAnimationFrame(step);
}

/**
 * AB#4647: "Back to top" affordance for the Browse detail view.
 *
 * The button is visually hidden until the guideline header has
 * scrolled out of view, then fades in at the bottom-right of the
 * viewport. On click it returns the header into view, which
 * works whether the host page scrolls the window or the widget
 * sits inside a scrollable parent container (scrollIntoView
 * walks the ancestor chain). The visibility check uses an
 * IntersectionObserver on the header element so it tracks
 * whichever ancestor actually scrolls — no scroll-event polling
 * and no assumption about which element holds the scroll bar.
 *
 * Bound to a target ref so it is scoped to the detail-view
 * header; the list view does not render this button (the list
 * is short enough not to need one).
 */
function ScrollToTopButton({
  targetRef,
}: {
  targetRef: React.RefObject<HTMLElement | null>;
}): React.ReactElement | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target = targetRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Show the button once the header is fully out of view.
        setVisible(!entry.isIntersecting);
      },
      // Small bottom rootMargin gives the toggle some hysteresis so
      // the button does not flicker right at the boundary as the
      // smooth-scroll animation crosses it.
      { threshold: 0, rootMargin: '0px 0px -40px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [targetRef]);

  if (!visible) return null;

  return (
    <button
      type="button"
      data-galen-browse-scroll-top
      aria-label="Back to top"
      onClick={() => {
        // Resolve a single scroll target rather than letting
        // ``scrollIntoView`` animate every scrollable ancestor at
        // once (multi-container animation is what made the
        // original implementation feel jerky on long guidelines),
        // then drive that container with a deterministic rAF
        // animation — the native ``behavior:'smooth'`` path could
        // bail mid-flight on long distances when the browser's
        // smooth-scroll heuristics interpreted an unrelated scroll
        // event as a user interruption.
        const target = targetRef.current;
        const scroller = findScrollContainer(target) ?? window;
        smoothScrollToTop(scroller);
      }}
      style={{
        position: 'fixed',
        right: '1.25rem',
        bottom: '1.25rem',
        width: '2.75rem',
        height: '2.75rem',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: '50%',
        background: 'var(--galen-primary, hsl(199, 89%, 48%))',
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
        cursor: 'pointer',
        // Sit below the BrowseToc overlay (zIndex 2147483000 / 001)
        // so an open TOC drawer still covers it.
        zIndex: 2147482900,
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 19V5" />
        <path d="m5 12 7-7 7 7" />
      </svg>
    </button>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const BrowseSurface: React.FC<BrowseSurfaceProps> = ({
  client,
  onError,
  initialGuidelineId,
  initialSectionId,
}) => {
  const [items, setItems] = useState<BrowseGuidelineListItem[] | null>(null);
  // null = list view; a number = detail view for that guideline.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<BrowseGuidelineDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // One-shot guard so the deep-link auto-open fires exactly once.
  const deepLinkOpened = useRef(false);
  // Section to scroll to once the detail DOM is present; cleared
  // after the scroll fires.
  const pendingScrollSectionId = useRef<number | null>(
    initialSectionId ?? null,
  );
  // AB#4647: ref to the detail-view header so the "back to top"
  // button can scroll it back into view and observe its visibility.
  const detailHeaderRef = useRef<HTMLElement | null>(null);

  // Initial list fetch. Wire shape: backend's GET
  // /v1/browse/guidelines is `response_model=list[...]` — a flat
  // array, no envelope (AB#4387). Tolerate a non-array by collapsing
  // to the empty-list state rather than crashing in `items.map`.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await client.getJson<BrowseGuidelineListItem[]>(
          '/api/v1/browse/guidelines',
        );
        if (!cancelled) {
          setItems(Array.isArray(response) ? response : []);
        }
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof Error ? e : new WidgetError(String(e));
        setErrorMessage(explainWidgetError(err));
        onError?.(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, onError]);

  const openGuideline = useCallback(
    async (id: number) => {
      setSelectedId(id);
      setDetailLoading(true);
      setDetail(null);
      setErrorMessage(null);
      try {
        const response = await client.getJson<BrowseGuidelineDetail>(
          `/api/v1/browse/guidelines/${id}`,
        );
        setDetail(response);
        // WIDGET-V1-R25: fire link_open on successful guideline open.
        void trackWidgetUsage(client, 'browse', 'link_open', {
          document_id: String(response.guidelineId),
          guideline_id: String(response.guidelineId),
          guideline_name: response.guidelineName,
          external_guideline_url: response.externalGuidelineURL ?? undefined,
        });
      } catch (e) {
        const err = e instanceof Error ? e : new WidgetError(String(e));
        setErrorMessage(explainWidgetError(err));
        onError?.(err);
      } finally {
        setDetailLoading(false);
      }
    },
    [client, onError],
  );

  const backToList = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
    setDetailLoading(false);
    setErrorMessage(null);
    pendingScrollSectionId.current = null;
  }, []);

  // Deep-link auto-open (the in-place BrowseLink swap).
  useEffect(() => {
    if (initialGuidelineId == null) return;
    if (deepLinkOpened.current) return;
    deepLinkOpened.current = true;
    void openGuideline(initialGuidelineId);
  }, [initialGuidelineId, openGuideline]);

  // BROWSE-V1-R05/R07/R08: narrow the upstream fullContent payload
  // once per detail load. The helpers drop malformed entries
  // silently so a partially-broken payload still renders.
  const sections = useMemo(
    () => (detail ? extractSectionContent(detail) : []),
    [detail],
  );
  const colourOption = useMemo(
    () => (detail ? extractRecommendationColourOption(detail) : null),
    [detail],
  );
  const references = useMemo(
    () => (detail ? extractReferenceContent(detail) : []),
    [detail],
  );

  // Once the detail (and its section DOM) is rendered, scroll to the
  // deep-linked section. Deferred a tick so `getElementById`
  // resolves against the committed DOM. One-shot.
  useEffect(() => {
    if (detail == null) return;
    const sectionId = pendingScrollSectionId.current;
    if (sectionId == null) return;
    const handle = setTimeout(() => {
      const el = document.getElementById(`cnt_${sectionId}`);
      if (el != null && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'start' });
      }
      pendingScrollSectionId.current = null;
    }, 0);
    return () => clearTimeout(handle);
  }, [detail]);

  const inDetail = selectedId != null;

  return (
    <div
      data-galen-surface="browse"
      style={{
        ...SURFACE_WRAPPER_STYLE,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      {!inDetail && (
        <div data-galen-browse-list-view>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700 }}>
            Browse Guidelines
          </h1>
          <p
            style={{
              margin: '0.25rem 0 0.9rem',
              fontSize: '0.9rem',
              color: 'var(--galen-muted, #475569)',
            }}
          >
            Clinical guidelines available in the local library.
          </p>
          {errorMessage !== null && <SurfaceErrorBanner message={errorMessage} />}
          <div
            data-galen-browse-list
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            {items === null && (
              <div style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                Loading guidelines…
              </div>
            )}
            {items !== null && items.length === 0 && (
              <div
                style={{
                  padding: '0.75rem',
                  fontSize: '0.9rem',
                  color: 'var(--galen-muted, #475569)',
                }}
              >
                No guidelines available.
              </div>
            )}
            {items?.map((item) => (
              <button
                key={item.guidelineId}
                type="button"
                data-galen-browse-list-item
                onClick={() => void openGuideline(item.guidelineId)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.7rem 0.85rem',
                  border: '1px solid var(--galen-border, #e2e8f0)',
                  borderRadius: 'var(--galen-border-radius, 0.5rem)',
                  background: 'var(--galen-background, #ffffff)',
                  color: 'inherit',
                  font: 'inherit',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {item.guidelineName}
                </div>
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--galen-muted, #475569)',
                    marginTop: '0.2rem',
                  }}
                >
                  {item.publishDate && (
                    <>Published {formatDate(item.publishDate)}</>
                  )}
                  {item.publishDate &&
                    item.updateDate &&
                    item.updateDate !== item.publishDate && (
                      <span style={{ margin: '0 0.4rem' }} aria-hidden="true">
                        ·
                      </span>
                    )}
                  {item.updateDate && item.updateDate !== item.publishDate && (
                    <>Updated {formatDate(item.updateDate)}</>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {inDetail && (
        <div data-galen-browse-detail-view>
          {/* AB#4408: only show the in-Browse "← Back to guidelines"
              when Browse is the *primary* surface (the user came in
              via the guideline list). When mounted in-place via a
              deep-link from a citation/result (initialGuidelineId
              set), this is "show me this one guideline" mode — the
              SurfaceHost "← Back to {surface}" bar above is the way
              back, so no internal list/back here. */}
          {initialGuidelineId == null && (
            <button
              type="button"
              data-galen-browse-back
              onClick={backToList}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '0.85rem',
                color: 'var(--galen-primary, hsl(199, 89%, 48%))',
                padding: '0.1rem 0.25rem',
                marginBottom: '0.5rem',
              }}
            >
              <span aria-hidden="true">&larr;</span> Back to guidelines
            </button>
          )}

          {errorMessage !== null && <SurfaceErrorBanner message={errorMessage} />}
          {detailLoading && (
            <div style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
              Loading guideline…
            </div>
          )}
          {detail && !detailLoading && (
            <article data-galen-browse-detail>
              <header
                ref={detailHeaderRef}
                data-galen-browse-detail-header
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <h1
                    style={{
                      margin: 0,
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    {detail.guidelineName}
                  </h1>
                  <p
                    style={{
                      margin: '0.25rem 0 0',
                      fontSize: '0.85rem',
                      color: 'var(--galen-muted, #475569)',
                    }}
                  >
                    {detail.publishDate && (
                      <>Published {formatDate(detail.publishDate)}</>
                    )}
                    {detail.publishDate &&
                      detail.updateDate &&
                      detail.updateDate !== detail.publishDate && (
                        <span style={{ margin: '0 0.4rem' }} aria-hidden="true">
                          ·
                        </span>
                      )}
                    {detail.updateDate &&
                      detail.updateDate !== detail.publishDate && (
                        <>Updated {formatDate(detail.updateDate)}</>
                      )}
                  </p>
                </div>
                {/* AB#4402 / BROWSE-V1-R06: a small "Contents (N)"
                    control at title level that opens the TOC as a
                    pop-over drawer. */}
                {sections.length > 0 && <BrowseToc sections={sections} />}
              </header>

              {detail.externalGuidelineURL && (
                <p style={{ margin: '0.5rem 0 0' }}>
                  <a
                    href={detail.externalGuidelineURL}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-galen-browse-external-link
                    style={{
                      fontSize: '0.9rem',
                      color: 'var(--galen-primary, #2563eb)',
                    }}
                  >
                    Open upstream source
                  </a>
                </p>
              )}

              {sections.length > 0 ? (
                <div
                  data-galen-browse-body
                  style={{
                    marginTop: '0.9rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <div data-galen-browse-sections>
                    {sections.map((section) => (
                      <SectionContent
                        key={section.sectionId}
                        section={section}
                        colourOption={colourOption}
                      />
                    ))}
                  </div>
                  <ReferencesList references={references} />
                </div>
              ) : (
                <div
                  data-galen-browse-empty-body
                  style={{
                    marginTop: '0.9rem',
                    padding: '0.75rem',
                    fontSize: '0.85rem',
                    color: 'var(--galen-muted, #475569)',
                    border: '1px dashed var(--galen-border, #e2e8f0)',
                    borderRadius: 'var(--galen-border-radius, 0.5rem)',
                  }}
                >
                  No section content for this guideline in the current sync
                  snapshot.
                </div>
              )}
              {/* AB#4647: viewport-fixed back-to-top button.
                  Rendered inside the detail-ready block so it only
                  mounts once the header element exists; the button
                  reveals itself when the header scrolls out of
                  view. */}
              <ScrollToTopButton targetRef={detailHeaderRef} />
            </article>
          )}
        </div>
      )}
    </div>
  );
};
